// PHASE 11C-1 CORRECTION (Phase 5, IDENTITY CORRECTION second round).
//
// Narrowly-scoped, Unicode-safe validator for one opaque uid used as a
// single `/user/:id` path segment (see NativeChatRouteBoundary.js's own
// USER_PROFILE navigation-event handling, the sole caller).
//
// NOT REUSED FROM ROUTE SYNTAX: the previous validator here
// (`^[A-Za-z0-9_-]+$`) was ChatRouteParser.java's own EXTERNAL ROUTE-INPUT
// grammar (see that class's own doc comment) - a policy for what a `/chat`
// URL SEGMENT may look like, never a claim about what a Firebase uid may
// contain. NativeChatDocumentIdResolver.java's own doc comment explicitly
// proves and preserves (since Phase 11a) that a real, legitimately-
// resolved uid may contain "_", "-", or non-ASCII/Unicode content (its own
// worked example: "zzz_élan"). Reapplying the route-syntax grammar here
// would have silently rejected a perfectly valid target uid. This
// validator instead DENIES only what is unsafe for THIS navigation
// boundary specifically, and otherwise preserves arbitrary legitimate uid
// content, Unicode included.
//
// A value that fails this check is never partially trusted or sanitized -
// see NativeChatRouteBoundary.js's own doc comment: USER_PROFILE simply
// never navigates for it.
//
// REUSED (PHASE 11C-1 CORRECTION, route-producer canonicalization) by
// src/navigation/nativeChatRouteBuilder.js for its own Mode.NEW proposed-
// other-uid validation (identical single-uid rules/bound) - that module
// supplies its own longer bound only for the SEPARATE Mode.EXISTING
// composite-document-id case, never for this single-uid one.

const MAX_UID_LENGTH = 128; // Firebase Authentication's own documented uid length bound.

const FORBIDDEN_EXACT_VALUES = new Set([".", ".."]);

// A leading URI scheme ("https:", "javascript:", "a+b.c-d9:", and a
// one-character scheme like "a:") is never a legitimate uid - mirrors
// RouteAllowList.java's own documented rejection of scheme-prefixed
// input.
//
// PHASE 11C-1 CORRECTION (final narrow correction, URI-scheme regression
// revert): a prior round changed this pattern's trailing quantifier from
// "*" (zero-or-more) to "+" (one-or-more), reasoning from a printable-
// ASCII matrix failure on "a:b". That change was itself wrong and has
// been reverted. RFC 3986 defines a scheme as
// ALPHA *( ALPHA / DIGIT / "+" / "-" / "." ) - the "*" means ZERO or more
// characters after the required initial ALPHA, so "a:" alone is already
// a syntactically complete, valid scheme name; "a:b" is scheme-prefixed
// text. Rejecting scheme-prefixed logical identifiers (including
// one-character schemes) is this codebase's own established policy -
// see RouteAllowList.java's identical rejection. Whether a colon is safe
// to ALLOW once confined to a single already-percent-encoded path
// segment is a SEPARATE security-policy question this fix does not
// decide; until that policy is deliberately revisited (with its own
// audit, threat analysis, and consumer review), scheme-prefixed values
// remain rejected outright, one-character schemes included.
const URI_SCHEME_PATTERN = /^[a-zA-Z][a-zA-Z0-9+.-]*:/;

function hasControlCharacter(value) {
  for (let i = 0; i < value.length; i++) {
    const code = value.charCodeAt(i);
    if (code <= 0x1f || code === 0x7f) {
      return true; // C0 controls (NUL included) and DEL.
    }
  }
  return false;
}

function hasUnpairedSurrogate(value) {
  for (let i = 0; i < value.length; i++) {
    const code = value.charCodeAt(i);
    const isHigh = code >= 0xd800 && code <= 0xdbff;
    const isLow = code >= 0xdc00 && code <= 0xdfff;
    if (isHigh) {
      const next = i + 1 < value.length ? value.charCodeAt(i + 1) : 0;
      if (!(next >= 0xdc00 && next <= 0xdfff)) {
        return true; // a high surrogate with no following low surrogate.
      }
      i += 1; // consume the valid pair - the low surrogate is never re-examined on its own.
    } else if (isLow) {
      return true; // a low surrogate not immediately preceded by (and consumed with) a high surrogate.
    }
  }
  return false;
}

/**
 * @param {unknown} value
 * @param {number} [maxLength] defaults to a single Firebase Authentication
 *   uid's own documented bound (128) - see
 *   src/navigation/nativeChatRouteBuilder.js's own doc comment for why a
 *   composite chat-document-id caller passes a longer bound instead of
 *   duplicating this whole function.
 * @returns {boolean} true only if `value` is safe to use, verbatim, as one
 *   opaque path segment (still to be encoded exactly once by the caller -
 *   see encodeUserProfilePathSegment).
 */
export function isSafeOpaqueUidSegment(value, maxLength = MAX_UID_LENGTH) {
  if (typeof value !== "string" || value.length === 0) {
    return false;
  }
  if (value.length > maxLength) {
    return false;
  }
  if (value !== value.trim()) {
    return false; // no leading/trailing whitespace.
  }
  if (FORBIDDEN_EXACT_VALUES.has(value)) {
    return false; // literal dot-segment.
  }
  if (value.includes("/") || value.includes("\\")) {
    return false;
  }
  if (value.includes("?") || value.includes("#")) {
    return false;
  }
  // PHASE 11C-1 CORRECTION (Phase 6): a blanket "%" rejection used to
  // live here. It is REMOVED - this app's actual UID/storage contract
  // does not forbid "%" as UID content, and this value is NEVER
  // percent-decoded anywhere in this module - it is only ever
  // encodeURIComponent'd EXACTLY ONCE by encodeUserProfilePathSegment
  // below, and decoded EXACTLY ONCE by React Router's own useParams() on
  // the receiving UserProfilePage.js route. A logical uid equal to the
  // literal string "%2e%2e" therefore encodes once to "%252e%252e" and
  // decodes once back to the literal "%2e%2e" - it can never become ".."
  // through this pipeline, because only one decode pass ever happens.
  // The FORBIDDEN_EXACT_VALUES check above still independently rejects a
  // logical uid that IS ITSELF exactly "." or ".." (encodeURIComponent
  // does not escape "." at all, and path normalization could otherwise
  // treat those two specific values structurally).
  if (value.includes("://") || URI_SCHEME_PATTERN.test(value)) {
    return false;
  }
  if (hasControlCharacter(value)) {
    return false;
  }
  if (hasUnpairedSurrogate(value)) {
    return false;
  }
  return true;
}

/**
 * Encodes an already-{@link isSafeOpaqueUidSegment}-validated uid EXACTLY
 * ONCE for interpolation as a `/user/:id` path segment - mirrors this
 * exact route's own established encoding convention elsewhere in this
 * codebase (e.g. UsersPage.js's own `/user/${encodeURIComponent(row.uid)}`).
 * React Router's own `useParams()` decodes a matched path segment exactly
 * once (via its internal `decodeURIComponent`), so this single encode
 * pass round-trips correctly on the receiving UserProfilePage.js route
 * without ever double-encoding/double-decoding.
 *
 * @param {string} validatedUid a value that has already passed {@link isSafeOpaqueUidSegment}.
 */
export function encodeUserProfilePathSegment(validatedUid) {
  return encodeURIComponent(validatedUid);
}
